/**
 * The market page, read-only (Phase 4).
 *
 * Anonymous visitor: no wallet, no trading, no roll. The §5.1 cold load end to
 * end, then a live page that runs on the socket alone.
 *
 * **First meaningful paint comes from one response.** `Runtime.start()` seeds
 * the channel stores from `GET /markets/current?include=orderbook,trades,candles`,
 * so the strip, the countdown, the ladder, the tape, the chart and the strike
 * line are all on screen before the socket has said anything. If the WebSocket
 * never opens, this page is a complete and correct static snapshot behind a
 * `STALE` treatment — nothing below waits on it.
 *
 * **Every panel reads a store, not a fetch result.** The cold-load response goes
 * through the same `StoreSet.apply` path the socket's own snapshots take, which
 * is why no component here branches on "has the socket landed yet".
 *
 * **This component subscribes to `market.{id}` and nothing else.** Every other
 * channel is subscribed by the panel that renders it (`panels.tsx`), because a
 * page that re-renders at the union of every channel's rate is the frame budget
 * spent on reconciliation rather than paint.
 *
 * The ticket column is still Phase 6 and `<ResolutionPanel>` still Phase 7: a
 * market expiring on this page renders its pill honestly from the state machine
 * and stops there.
 */

import { Suspense, lazy, useCallback, useEffect, useState } from 'react'
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { AppShell, Header, MarketStrip, NotificationTray, Panel, PanelGrid } from '@/components/layout'
import { ClockPill, ConnectionBanner } from '@/components/feedback'
// `<ConnectButton>` reads `Runtime.identityStore`, which is viem-free, so this
// import does not pull the wallet chunk onto the anonymous cold-load path. The
// chunk arrives on hover or click — see `wallet/lazy.ts`.
import { ConnectButton } from '@/components/wallet/ConnectButton'
import { BottomTabs } from '@/components/layout/BottomTabs'
import {
  Button,
  EmptyState,
  Glyph,
  Sheet,
  TabPanel,
  Tabs,
} from '@/components/primitives'
import {
  Countdown,
  LastMarketCard,
  ProToggle,
  SettlementReceiptStack,
  TIMEFRAMES,
  type Timeframe,
} from '@/components/market'
import { OrderTicket } from '@/components/ticket'
import { FillsTable, OpenOrdersTable, PositionsTable, SettledTable } from '@/components/tables'
import {
  AssetSelector,
  BookPanel,
  ProBookPanel,
  ChartPanel,
  LiveMarketStatusPill,
  RailPanel,
  ResolutionSurface,
  StripStats,
  StrikeMeterPanel,
  TapePanel,
} from './panels'
import { ArchivedMarket } from './ArchivedMarket'
import { BalanceChip } from '@/components/portfolio'
import { useAtLeast } from '@/app/use-breakpoint'
import { useMarket, useRuntime, useRuntimeState } from '@/app/use-runtime'
import { useStore } from '@/app/use-store'
import { bottomTabRequest, uiStore } from '@/stores/ui'
import { buildMarketId, isMarketId, previousMarketId, seriesIdFor, symbolFor, type Asset } from '@/lib/market-id'
import { readStoredFlag, writeStoredFlag } from '@/app/stored'
import { cx } from '@/lib/cx'
import type { MarketState } from '@/domain/lifecycle'
import type { Outcome } from '@/lib/decimal'
import type { TabItem } from '@/components/primitives'

type BottomTab = 'positions' | 'orders' | 'fills' | 'history' | 'settled'
type BookTab = 'book' | 'trades' | 'positions' | 'orders' | 'chat'

/**
 * The troll box (§F137), behind an `import()`: open by default from 1280px up
 * (§F141), so its chunk follows the first paint on most visits and is still no
 * part of the cold load's budget (invariant 19). Its channel is subscribed by the
 * runtime regardless, so the room is already full when it opens.
 */
const ChatPanel = lazy(() => import('@/components/chat/ChatPanel'))

/** The account's tables. The troll box sits beside them, not among them (§F141). */
const BOTTOM_TABS: readonly TabItem<BottomTab>[] = [
  { id: 'positions', label: 'Positions' },
  { id: 'orders', label: 'Open Orders' },
  { id: 'fills', label: 'Fills' },
  { id: 'history', label: 'History' },
  { id: 'settled', label: 'Settled' },
]

const BOOK_TABS: readonly TabItem<BookTab>[] = [
  { id: 'book', label: 'Orderbook' },
  { id: 'trades', label: 'Trades' },
]

/**
 * Below 768px the bottom tables have no row, so the two a trader needs while a
 * market runs join the book's tabs, and so does the troll box, which lives in
 * that row everywhere else. Fills, History and Settled do not: they are about
 * markets that have already happened, and `/portfolio` and `/history` are
 * where there is room for them.
 */
const PHONE_BOOK_TABS: readonly TabItem<BookTab>[] = [
  ...BOOK_TABS,
  { id: 'positions', label: 'Positions' },
  { id: 'orders', label: 'Orders' },
  { id: 'chat', label: 'Troll box' },
]

/**
 * The chart's timeframe, persisted per device.
 *
 * **`market` is the default**, and it is the strongest form of the reason `1s`
 * used to be: the product's own object is sixty seconds long, so a first-time
 * visitor should land looking at exactly the minute they can trade rather than
 * at a rolling window with that minute in one corner of it.
 *
 * A returning trader who moved to `5m` still gets `5m` back — the chart is of
 * BTC, and what they want to see of BTC does not reset every minute (§F19). So
 * only the *default* moved; `readTimeframe` validates the held value against
 * `TIMEFRAMES`, which an added member widens rather than invalidates, and there
 * is deliberately no key bump and no migration.
 */
const TIMEFRAME_KEY = 'ud.chart.timeframe'
const DEFAULT_TIMEFRAME: Timeframe = 'market'

/** Whether `<LastMarketCard>` is folded into its one row, per device. Open by
 *  default: the card is the answer to "did I win", and a first visit should
 *  see where that answer lives. */
const LAST_MARKET_COLLAPSED_KEY = 'ud.last-market.collapsed'

/**
 * Whether the troll box is open beside the tables, per device (§F141). Open by
 * default from `TROLL_BOX_ROOMY_WIDTH` up: the room is part of the page, and closing
 * it gives the tables the row. Below that the tables' 60% truncates their headers
 * (323px at 1120, where the ticket takes a column), so it starts closed. Only the
 * default reads the window; a choice the user made always wins.
 */
const TROLL_BOX_OPEN_KEY = 'ud.troll-box.open'
const TROLL_BOX_ROOMY_WIDTH = 1280

/**
 * `/trade/:asset?anchor=` — and the anchor decides which of two pages this is.
 *
 * **Until §F28 it decided nothing.** `router.tsx` has called the anchor the
 * canonical deep link since Phase 3, and the only thing that read it was the
 * asset switch, preserving it. A History link into a settled market therefore
 * opened the *live* market and looked entirely correct, which is the fifth exit
 * criterion failing in the way that is hardest to notice.
 *
 * An anchor in the past renders `<ArchivedMarket>`: one cached request, nothing
 * subscribed, no countdown. An anchor that is the live one, or absent, renders
 * the market page below, unchanged.
 *
 * **A deep link waits for the cold load before choosing**, because choosing
 * wrongly for 200 ms means flashing the live market at someone who asked for a
 * historical one. A plain `/trade/btc` is unaffected — there is nothing to
 * decide, so it renders immediately as it always has.
 */
export function TradeRoute() {
  const params = useParams<{ asset: string }>()
  const [search] = useSearchParams()
  const runtime = useRuntime()
  const routeAsset: Asset = params.asset === 'eth' ? 'eth' : 'btc'

  useEffect(() => {
    void runtime.start(symbolFor(routeAsset))
  }, [runtime, routeAsset])

  const state = useRuntimeState()
  // An asset the venue does not run: an old link, or a bookmark (§F138).
  if (state.phase === 'READY' && !state.assets.includes(routeAsset)) {
    return <Navigate to={`/trade/${state.asset}`} replace />
  }
  const anchorParam = Number(search.get('anchor'))
  const hasAnchor = Number.isInteger(anchorParam) && anchorParam > 0

  if (hasAnchor) {
    // The live anchor is what "past" is measured against, and it comes from the
    // runtime rather than from a clock: `Date.now()` appears only inside
    // `lib/clock/` (invariant 7), and the anchor grid is the venue's.
    if (state.phase !== 'READY') {
      return (
        <AppShell header={<Header account={<ConnectButton store={runtime.identityStore} />} />}>
          <div className="min-h-0 flex-1 bg-bg-canvas" />
        </AppShell>
      )
    }
    if (anchorParam < state.anchorS) {
      return (
        <AppShell
          notice={<ConnectionBanner />}
          header={
            <Header
              balance={<BalanceChip />}
              notifications={<NotificationTray />}
              account={<ConnectButton store={runtime.identityStore} />}
            />
          }
        >
          <ArchivedMarket marketId={buildMarketId(seriesIdFor(routeAsset), anchorParam)} />
        </AppShell>
      )
    }
  }

  return <LiveMarket asset={routeAsset} />
}

function LiveMarket({ asset: routeAsset }: { asset: Asset }) {
  const [search] = useSearchParams()
  const navigate = useNavigate()
  const runtime = useRuntime()

  const state = useRuntimeState()

  // The asset switch (§5.5): one batched subscribe/unsubscribe at the **same
  // anchor** plus one `GET /markets/current`. `market.{eth N}` was already
  // subscribed at cold load, so the countdown does not jump and the strike line
  // does not blank.
  useEffect(() => {
    if (state.phase === 'READY' && state.asset !== routeAsset) void runtime.switchAsset(routeAsset)
  }, [runtime, routeAsset, state.phase, state.asset])

  const marketId = state.marketId || null
  const market = useMarket(marketId)
  // **No clock and no index prices here.** Both are store subscriptions and this
  // is the screen: together they re-rendered the whole market page about
  // fourteen times a second — the clock's 4 Hz plus two `index.*` channels at
  // 5 Hz each — for two prices on the asset control and two progress bars, and
  // every memo below reconciled with them (`docs/17`, invariant 2).
  // `<AssetSelector>`, `<LiveMarketStatusPill>`, `<BookPanel>` and
  // `<OrderTicket>` each hold their own now, and a banned-name rule scoped to
  // `*Route.tsx` is what stops the next one arriving here.

  /**
   * The bottom tab, and the one thing outside this route that may change it.
   *
   * §9.2.5's "while you were away" summary links to Settled, and the tab lives
   * here in `useState`. Derived during render rather than set from an effect:
   * the newest of the two wins, so a request that arrives after the user's last
   * choice takes the tab and any click afterwards takes it back. The **nonce**
   * is what is compared, because asking twice for the same tab is two events
   * and a second reconnect a minute later must not be silent.
   */
  const tabRequest = useStore(bottomTabRequest)
  const [chosenTab, setChosenTab] = useState<{ tab: BottomTab; nonce: number }>({
    tab: 'positions',
    nonce: 0,
  })
  const tab =
    tabRequest.tab !== null && tabRequest.nonce > chosenTab.nonce
      ? (tabRequest.tab as BottomTab)
      : chosenTab.tab
  const setTab = useCallback(
    (next: BottomTab) => setChosenTab({ tab: next, nonce: bottomTabRequest.state.nonce }),
    [],
  )
  const [bookTab, setBookTab] = useState<BookTab>('book')
  /** Which sticky button opened the sheet — `null` while it is closed. The
   *  ticket opens on that outcome, so BUY DOWN is a DOWN ticket. */
  const [sheetOutcome, setSheetOutcome] = useState<Outcome | null>(null)
  const [timeframe, setTimeframe] = useState<Timeframe>(readTimeframe)
  const ticketHasItsOwnColumn = useAtLeast('lg')
  /**
   * The bottom tables' row exists from 768px up. Below it they are not rendered
   * at all — not hidden with CSS, which left `<PositionsTable>` mounted and
   * subscribed behind a `display: none` — and Positions and Orders become tabs
   * of the book panel instead.
   */
  const tablesHaveARow = useAtLeast('md')
  /**
   * Pro mode — one clickable ladder in place of the book and the ticket.
   *
   * `useStore` rather than a channel hook, so the rule that keeps subscriptions
   * out of routes does not apply and nothing here re-renders at a channel's
   * rate: `uiStore` is written only by a user gesture.
   *
   * **`&& ticketHasItsOwnColumn` is the whole of the breakpoint rule.** The pro
   * ladder spans the orderbook's column and the ticket's, and `lg` is precisely
   * where the second of those exists at all. Below it the flag persists unused
   * and the standard layout renders — which is also what a user gets by
   * narrowing a window, without losing the preference.
   */
  const proMode = useStore(uiStore).proMode && ticketHasItsOwnColumn
  /** Which resolving market the user closed the card on, below `lg`. Keyed by
   *  `market_id`, so closing one does not suppress the next one a minute later. */
  const [dismissedResolution, setDismissedResolution] = useState<string | null>(null)
  const [lastMarketCollapsed, setLastMarketCollapsed] = useState(() =>
    readStoredFlag(LAST_MARKET_COLLAPSED_KEY, false),
  )
  const [trollBoxOpen, setTrollBoxOpen] = useState(() =>
    readStoredFlag(TROLL_BOX_OPEN_KEY, globalThis.innerWidth >= TROLL_BOX_ROOMY_WIDTH),
  )
  const toggleTrollBox = useCallback(() => {
    const next = !trollBoxOpen
    writeStoredFlag(TROLL_BOX_OPEN_KEY, next)
    setTrollBoxOpen(next)
  }, [trollBoxOpen])
  const toggleLastMarket = useCallback(() => {
    const next = !lastMarketCollapsed
    writeStoredFlag(LAST_MARKET_COLLAPSED_KEY, next)
    setLastMarketCollapsed(next)
  }, [lastMarketCollapsed])

  const onTimeframe = useCallback((next: Timeframe) => {
    setTimeframe(next)
    try {
      globalThis.localStorage?.setItem(TIMEFRAME_KEY, next)
    } catch {
      /* a blocked storage is not worth an error surface */
    }
  }, [])

  // The URL carries the anchor alongside the asset so the switch is a parameter
  // change rather than a navigation, and so a support link is reproducible: at
  // 2,880 markets a day, "the BTC market at 21:46" has to be addressable.
  const switchAsset = useCallback(
    (next: Asset) => {
      const anchor = search.get('anchor')
      void navigate(`/trade/${next}${anchor ? `?anchor=${anchor}` : ''}`, { replace: true })
    },
    [navigate, search],
  )

  const marketState = (market?.state as MarketState | undefined) ?? 'SCHEDULED'
  const anchorAtMs = numberOf(market, 'anchor_ms') ?? state.anchorS * 1000
  // The fallback is `anchor_ms` since register §F24 — the strike window closes
  // at the anchor, so a market opens as it anchors. It was `anchor + 13_000`.
  // The payload's `open_at_ms` still wins: a late strike slips it, and the whole
  // point of reading it rather than computing it is that the client is correct
  // either way.
  const openAtMs = numberOf(market, 'open_at_ms') ?? anchorAtMs
  const expiryAtMs = numberOf(market, 'expiry_at_ms') ?? anchorAtMs + state.periodS * 1000
  // Read, never derived (invariant 11, §F26l). No fallback: there is no value to
  // guess, and guessing one would sign an order against a position that is not
  // this market's.
  const upTokenId = tokenIdOf(market, 'up_token_id')
  const downTokenId = tokenIdOf(market, 'down_token_id')
  // **`market !== undefined` is load-bearing, and §F24 is what exposed it.**
  // `marketState` defaults to `SCHEDULED` before the market object arrives, so
  // this predicate was true for the first few hundred milliseconds of every cold
  // load. That used to render `opens in 5s` — wrong, but plausible enough to
  // survive review. With `open_at_ms == anchor_ms` it renders `opens in 0s` on a
  // market that has been trading for forty seconds, which is not survivable.
  //
  // The empty state must be keyed on knowing the market is pre-open, not on not
  // knowing anything about it.
  const preTrade = market !== undefined && (marketState === 'FIXING' || marketState === 'SCHEDULED')

  // `STALE` is the socket's opinion, not a guess: panels keep their last known
  // values and say so, rather than blanking (§4.1, §9.2.1).
  //
  // **It is the panels' term and it is not the order-entry gate.** A `seq` gap
  // and a checksum mismatch both leave the connection `OPEN` while one market's
  // ladder stops being maintained, which this predicate cannot see and must not
  // pretend to: the chart and the countdown are fine in that state. The ticket
  // adds the book's own sync state to this — `<OrderTicket>`'s `bookStale`,
  // read off the store it already subscribes — and the ladder renders its own
  // pill from the same flag (`<OrderbookLadder>`).
  const stale = state.connection !== 'OPEN'
  // The book during `FIXING` says `opens in {n}s` — **never** `no liquidity`
  // (docs/07 §5). It has no depth because it is not open yet, which is a fact
  // about the clock rather than about the venue.
  const depthIsTabbed = !ticketHasItsOwnColumn
  const activeBookTab: BookTab =
    tablesHaveARow && (bookTab === 'positions' || bookTab === 'orders' || bookTab === 'chat')
      ? 'book'
      : bookTab

  /**
   * The strike meter and the form, without a frame.
   *
   * Framed twice it was titled twice: the sheet has its own title, padding and
   * scroll, and the column's `<Panel>` carried a second of each inside it — so
   * a phone read "Order ticket" twice and scrolled a box inside a box.
   */
  const ticketBody = (initialOutcome?: Outcome, linkBook = false) => (
    <div className="flex flex-col gap-3">
      <StrikeMeterPanel
        symbol={state.symbol}
        strike={market?.strike}
        stale={stale}
        pendingAtMs={marketState === 'OPEN' && market?.strike == null ? anchorAtMs : null}
      />
      <OrderTicket
        marketId={marketId}
        symbol={state.symbol}
        marketState={marketState}
        openAtMs={openAtMs}
        expiryAtMs={expiryAtMs}
        upTokenId={upTokenId}
        downTokenId={downTokenId}
        limits={runtime.tradingLimits}
        socketStale={stale}
        {...(initialOutcome ? { initialOutcome } : {})}
        // The column ticket, and only it, sizes the book's click to trade.
        linkBook={linkBook}
      />
    </div>
  )

  const ticket = (
    // **`scroll`, and its absence made the submit button unreachable.** The
    // body is `min-h-0 flex-1` with `overflow: visible` unless this is set, so
    // on any viewport where the ticket is taller than its column — a laptop at
    // 1080 tall is enough, with the strike meter, both segmented controls, the
    // size slider and the four payout rows stacked — the form did not clip, it
    // *spilled*, and the primary action landed underneath the bottom tab strip
    // where nothing could click it. Every other Panel on this route already
    // passes it.
    //
    // The page itself must never scroll (`Panel`'s own header), so the fix is
    // the panel's body and not a taller grid row: the ticket is the one column
    // whose content has no upper bound, because the payout block grows with the
    // quote and the error region below it grows with whatever the venue said.
    //
    // **The rest of the column, whatever the card says** (2026-10-13). The
    // last market's card under it is one height and folds only when the user
    // folds it, so the ticket's room changes with the window and the arrow and
    // never with live data; the ticket scrolls inside it and keeps its primary
    // button in view (`OrderTicket`).
    <Panel
      header="Order ticket"
      className="min-h-0 flex-1"
      scroll
    >
      <div className="p-3">{ticketBody(undefined, true)}</div>
    </Panel>
  )

  /**
   * The market that ended at the current anchor — read off the live market's
   * own `prev_market_id`, and off the anchor grid only until that has arrived.
   */
  const lastMarketId =
    stringOf(market, 'prev_market_id') ??
    (marketId !== null && isMarketId(marketId) && state.periodS > 0 ? previousMarketId(marketId, state.periodS) : null)

  /**
   * The secondary surface — market N resolving behind market N+1 (§6.3).
   *
   * `resolvingMarketId` comes off `RuntimeState` and is the *identity* only:
   * `<ResolutionPanel>` subscribes `market.{id}` itself, and
   * `<SettlementReceiptStack>` subscribes `settlements` itself, because a store
   * subscribed by the screen re-renders the screen (invariant 2). Nothing here
   * reads a channel.
   *
   * Mounted only below 1120px now, as the card above the tables (`aside`).
   * From 1120px the standard layout has `<LastMarketCard>` instead, and pro
   * mode has neither (2026-10-10).
   */
  const resolution = state.resolvingMarketId

  const book = (
    <BookPanel
      marketId={marketId}
      opensAtMs={preTrade ? openAtMs : null}
      // Click to trade sizes from the ticket's Shares box, so it exists only
      // where the ticket has a column beside the book. Everything below is what
      // a click needs to place an order — the props `<ProBookPanel>` takes.
      clickable={ticketHasItsOwnColumn}
      symbol={state.symbol}
      marketState={marketState}
      openAtMs={openAtMs}
      expiryAtMs={expiryAtMs}
      upTokenId={upTokenId}
      downTokenId={downTokenId}
      limits={runtime.tradingLimits}
      socketStale={stale}
      className="min-h-0 flex-1"
    />
  )
  const tape = <TapePanel marketId={marketId} />
  const chat = (
    <Suspense fallback={null}>
      <ChatPanel asset={state.asset} className="h-full" />
    </Suspense>
  )

  /**
   * The pro ladder's region: the bar and the ladder, and nothing under it.
   *
   * **No resolution panel and no receipt card either** (2026-10-10, the spec
   * owner's call). Each took the foot of the ladder for seconds after every
   * expiry — prices a click-trader aims at — and the series rail already says
   * which way each market went. The settlement toast stays: it floats, so it
   * costs the ladder no row.
   *
   * **No trades tape here.** The two columns this replaces carried one between
   * them, and in pro mode it is the wrong 240px: the ladder is the panel a
   * click-trader reads and the one whose rows are the click targets, so every
   * row the tape takes is a price that has to be scrolled to. The tape is still
   * a tab away below the chart, and the last trades stay legible from the book
   * itself.
   */
  const proColumn = (
    <>
      <ProBookPanel
        marketId={marketId}
        symbol={state.symbol}
        marketState={marketState}
        openAtMs={openAtMs}
        expiryAtMs={expiryAtMs}
        upTokenId={upTokenId}
        downTokenId={downTokenId}
        limits={runtime.tradingLimits}
        socketStale={stale}
        opensAtMs={preTrade ? openAtMs : null}
      />
      <SettlementReceiptStack showReceipt={false} />
    </>
  )

  /**
   * The book column, at the two widths it has: tabbed beside the chart below
   * 1120px, stacked over the tape above it.
   *
   * Lifted out of the `<PanelGrid>` call because pro mode makes the column set
   * conditional, and a prop that is sometimes absent has to be spread rather
   * than written inline.
   */
  const bookRegion = (
          depthIsTabbed ? (
            // 768–1119px: the orderbook and the tape become tabs beside the
            // chart, because two dense panels do not fit a 260px column.
            //
            // **The body scrolls and the tab bar does not.** `<Panel>`'s body is
            // a block by default, so the ladder — 24 fixed rows plus a header
            // and the mid row, about 576px — took its natural height inside a
            // `flex-1` cell that could not contain it and drew *over* the
            // resolution card and the tables below. `bodyClassName` makes the
            // body the flex column the two children need, `shrink-0` pins the
            // tabs, and the scroll belongs to the panel that overflows rather
            // than to the page, which never scrolls.
            //
            // Below 768px Positions and Orders join them (`PHONE_BOOK_TABS`), and
            // only the chosen one is mounted: an unmounted table holds no
            // subscription (invariant 2). A tab held from a phone width falls
            // back to the book when the window widens past `md`, where those
            // two have a row of their own.
            <Panel className="min-h-0 flex-1" bodyClassName="flex min-h-0 flex-col">
              <Tabs
                items={tablesHaveARow ? BOOK_TABS : PHONE_BOOK_TABS}
                value={activeBookTab}
                onChange={setBookTab}
                label="depth"
                className="shrink-0 px-2"
              />
              <TabPanel id={activeBookTab} active className="ud-scroll min-h-0 flex-1">
                {activeBookTab === 'book' ?
                  book
                : activeBookTab === 'trades' ?
                  tape
                : activeBookTab === 'chat' ?
                  chat
                : activeBookTab === 'positions' ?
                  <PositionsTable />
                : <OpenOrdersTable />}
              </TabPanel>
            </Panel>
          ) : (
            // ≥1120px: two panels stacked in the column, and the tape is its
            // own scroll rather than the tail of the ladder's. It was one
            // `scroll`ing panel holding both, so reading the tape scrolled the
            // book away — on the one panel a taker is watching while they do
            // it.
            <div className="flex min-h-0 flex-1 flex-col gap-px">
              <Panel className="min-h-0 flex-1" scroll>
                {book}
              </Panel>
              {/* **The tape's height is `PanelGrid`'s tables' height, deliberately** —
                  40% of the column, never under 240px — so whenever the chart's
                  region is not scrolled the tape's header lands on exactly the
                  line the Positions / Open Orders / Fills tabs do. Two literals
                  rather than one constant — a Tailwind class is a static
                  string — so an e2e test asserts the two tops are equal, which
                  is the only thing that keeps them so. */}
              <Panel header="TRADES" className="h-[max(calc(240*var(--ud-u)),40%)] shrink-0" scroll>
                {tape}
              </Panel>
            </div>
          )
  )

  return (
    <AppShell
      notice={<ConnectionBanner />}
      header={
        <Header
          // Two figures, never one summed number (invariant 9). The chip
          // subscribes the `balance` store itself, so a balance frame re-renders
          // a chip and not this page — which at 20 f/s through a settlement is
          // the difference between a header and a repaint of the ladder.
          balance={<BalanceChip />}
          notifications={<NotificationTray />}
          account={<ConnectButton store={runtime.identityStore} />}
        />
      }
      strip={
        <MarketStrip
          assetSelector={
            <AssetSelector value={state.asset} assets={state.assets} onChange={switchAsset} />
          }
          {...(state.marketId ? { marketId: state.marketId } : {})}
          stats={<StripStats marketId={marketId} />}
          // Pinned beside the stats, never inside their scroll: the one reading
          // on this strip a trader cannot do without (`<MarketStrip>`'s `status`).
          status={
            <>
              <LiveMarketStatusPill
                state={marketState}
                outcome={outcomeOf(market)}
                openAtMs={openAtMs}
                resolveEtaMs={numberOf(market, 'resolve_eta_ms')}
                // `FIXING` fills over `[strike_window_start, open_at]`;
                // `RESOLVING` over `[expiry, resolve_eta]`.
                //
                // **The `FIXING` start is read from the payload, not derived.**
                // It was `anchorAtMs`, which was correct while the strike window
                // ran forward from the anchor. Since §F24 it runs
                // `[anchor − 10s, anchor)` and `open_at_ms == anchor_ms`, so
                // `anchorAtMs` would make the bar a zero-width window and the
                // fraction meaningless.
                //
                // No fallback: a server that omits `strike_window_start_ms`
                // leaves the bar indeterminate rather than drawing a fraction
                // over a window nobody published. `preTrade` already requires a
                // market object, so this is null only if the field is missing.
                windowStartMs={
                  preTrade ? numberOf(market, 'strike_window_start_ms') : expiryAtMs
                }
                muted={stale}
              />
              {/* §9.2.8, beside the state it qualifies. A confidently wrong
                  clock is worse than a visibly uncertain one. */}
              <ClockPill />
            </>
          }
          /* The switch over the whole right-hand side of the page, which is
             what it switches: pro mode replaces the orderbook column and the
             ticket column together, so it cannot live in either of them —
             turning it on would take its own control off the screen. Only from
             1120px up, where there is a ticket column for a wide ladder to
             span; below that the ticket is a `<Sheet>` and the switch is not
             rendered rather than rendered inert. */
          actions={ticketHasItsOwnColumn ? <ProToggle /> : undefined}
          /* **No countdown in the strip.** It is the chart's underlay now — the
             clock as the ground the price moves over rather than a 56px control
             in the corner, which on a product whose object *is* a minute is
             where it always belonged. `<MarketStrip>` keeps the slot; this route
             stops filling it. */
        />
      }
      bottomBar={
        <div className="grid grid-cols-2 gap-2">
          <Button variant="up" size="lg" onClick={() => setSheetOutcome('UP')}>
            <Glyph name="up" /> BUY UP
          </Button>
          <Button variant="down" size="lg" onClick={() => setSheetOutcome('DOWN')}>
            <Glyph name="down" /> BUY DOWN
          </Button>
        </div>
      }
    >
      <PanelGrid
        // Only where the tables have a row (`tablesHaveARow`, above).
        {...(tablesHaveARow ? { bottom: (
          <div className="flex min-h-0 flex-1">
          <BottomTabs
            items={BOTTOM_TABS}
            value={tab}
            onChange={setTab}
            trailing={trollBoxOpen ? undefined : <TrollBoxToggle open={false} onToggle={toggleTrollBox} />}
            className="min-w-0"
          >
            {/* Each table subscribes its own private store. Rendering only the
                active one is not an optimisation — an unmounted table holds no
                subscription, so the two tabs the user is not looking at cost
                nothing at the `orders`/`fills` frame rate (invariant 2). */}
            {tab === 'positions' ? <PositionsTable /> : null}
            {tab === 'orders' ? <OpenOrdersTable /> : null}
            {tab === 'fills' ? <FillsTable /> : null}
            {/* Settled is a channel, not a paginated read (§F24f): `settlements`
                was already subscribed and already stored, and a dismissed toast
                must not be the only place a user could learn whether they won.
                History — and the cursor-paged archive behind
                `GET /portfolio/settlements` — is still Phase 8. */}
            {tab === 'settled' ? <SettledTable /> : null}
            {/*
              History is its own screen (`/history`): the merged feed is
              cursor-paged and belongs where there is room to page it. The market
              page keeps the tab as a signpost rather than a second copy — a
              paginated table inside a 200px bottom row would be a worse version of
              a screen that already exists.
            */}
            {tab === 'history' ? (
              <EmptyState action={{ label: 'Open History', onClick: () => navigate('/history') }}>
                Fills, settlements and fees, merged.
              </EmptyState>
            ) : null}
          </BottomTabs>
          {/* 40% of the row beside the tables, and the toggle stays at the row's
              right end either way, so it does not move under the pointer. */}
          {trollBoxOpen ? (
            <aside
              id="troll-box"
              aria-label="Troll box"
              className="flex w-[40%] shrink-0 flex-col border-l border-border-subtle bg-bg-surface"
            >
              <div className="flex h-[var(--ud-subheader-height)] shrink-0 justify-end border-b border-border-subtle px-3">
                <TrollBoxToggle open onToggle={toggleTrollBox} />
              </div>
              <div className="min-h-0 flex-1">{chat}</div>
            </aside>
          ) : null}
          </div>
        ) } : {})}
        /* Below 1120px the ticket has no column for the resolution to sit under,
           so it becomes a dismissible card above the tables. Checked at 700, 900
           and 1200px on the day it was built, because a placeholder hides the
           breakpoint bugs of everything around it (§F23d) and these two components
           were that placeholder for four phases. */
        aside={
          !ticketHasItsOwnColumn && resolution !== null && resolution !== dismissedResolution ? (
            <ResolutionSurface
              marketId={resolution}
              symbol={state.symbol}
              stale={stale}
              onDismiss={() => setDismissedResolution(resolution)}
              // One line below 768px, where the card would take the book.
              compact={!tablesHaveARow}
            />
          ) : undefined
        }
        chart={
          /* `bodyClassName` makes the panel body a flex column, and it is not
             cosmetic: `<Panel>`'s body is a block by default, so a `flex-1`
             child inside it has no flex parent to stretch against and falls back
             to its content height. The chart then sat at its 240px minimum with
             the rest of the column empty below it. */
          <Panel className="min-h-0 flex-1" bodyClassName="flex min-h-0 flex-col">
            <div className="flex min-h-0 flex-1 flex-col">
              <ChartPanel
                seriesId={state.seriesId}
                symbol={state.symbol}
                timeframe={timeframe}
                onTimeframeChange={onTimeframe}
                currentMarketId={marketId}
                // The clock, behind the candles (§2.1) — and since the strip
                // stopped carrying one, *the* clock: this is the announced
                // `role="timer"` as well as the picture.
                underlay={
                  <Countdown
                    state={marketState}
                    anchorAtMs={anchorAtMs}
                    openAtMs={openAtMs}
                    expiryAtMs={expiryAtMs}
                    clock={runtime.clock}
                    stale={stale}
                    underlay
                  />
                }
                // The market's own span, for the `market` timeframe. The same
                // two locals `<Countdown>` takes — read off the payload with
                // §F24's fallbacks — rather than a second copy derived here,
                // because two derivations of one window are two things that
                // can disagree.
                //
                // **Passed only once there is a market**, on the same reasoning
                // as `preTrade` above: before the payload lands `anchorAtMs`
                // falls back to `state.anchorS`, which is `0` until the runtime
                // is READY. That is not an anchor, it is the absence of one, and
                // a chart handed 1970 pins its pane a billion bars to the left
                // of its data. The chart falls back to the rolling window,
                // which is the correct render for "no market yet".
                {...(market ? { windowStartMs: anchorAtMs, windowEndMs: expiryAtMs } : {})}
                stale={stale}
              />
              <RailPanel
                seriesId={state.seriesId}
                marketId={marketId}
                periodS={state.periodS}
                compact={!tablesHaveARow}
                className="shrink-0 border-t border-border-subtle p-1_5"
              />
            </div>
          </Panel>
        }
        /* Only when it has a column. `PanelGrid` hides the ticket cell with
           `hidden lg:flex`, which is CSS and not the DOM — so passing it below
           1120px mounted a *second* live `<OrderTicket>` behind the sheet's,
           with its own book, ticker, balance, positions and orders
           subscriptions. Two of everything invariant 2 is about, on the
           smallest device. `PanelGrid`'s own header says the bottom two
           breakpoints "remove a column from the DOM"; this is what makes that
           true (register §F23c). */
        /* Pro mode replaces both right-hand regions with one. Passed as `wide`
           rather than as a `book` that happens to be wider, because `PanelGrid`
           hides the ticket cell with CSS and leaving a live `<OrderTicket>`
           behind the ladder would mount a second set of book, ticker, balance,
           positions and orders subscriptions (§F23c). */
        {...(proMode ?
          { wide: proColumn }
        : {
            book: bookRegion,
            ...(ticketHasItsOwnColumn ?
              {
                ticket: (
                  <div className="flex min-h-0 flex-1 flex-col gap-px">
                    {ticket}
                    {/* Under the ticket, never over it: its own height, at
                        most 45% of the column, or one row when folded.
                        It replaces the resolution panel and the receipt here —
                        the card itself goes RESOLVING → RESOLVED → SETTLED —
                        so the settlement toast is kept quiet for its market
                        and raised for any other (the other asset's). */}
                    <LastMarketCard
                      marketId={lastMarketId}
                      seriesId={state.seriesId}
                      symbol={state.symbol}
                      currentMarketId={marketId}
                      periodS={state.periodS}
                      collapsed={lastMarketCollapsed}
                      onToggle={toggleLastMarket}
                      stale={stale}
                      className={lastMarketCollapsed ? 'shrink-0' : 'max-h-[45%] shrink-0'}
                    />
                    <SettlementReceiptStack showReceipt={false} quietMarketId={lastMarketId} />
                  </div>
                ),
              }
            : {}),
          })}
      />

      {/* Below 1120px the ticket has no column, so it becomes a sheet rather
          than being hidden — a live order form rendered off-screen is worse
          than one that is not rendered (components.md §1). */}
      {!ticketHasItsOwnColumn ? (
        <Sheet open={sheetOutcome !== null} onClose={() => setSheetOutcome(null)} title="Order ticket">
          {ticketBody(sheetOutcome ?? undefined)}
        </Sheet>
      ) : null}
    </AppShell>
  )
}

// ---------------------------------------------------------------------------

/**
 * Looks like a tab and is a toggle. The room is open beside whichever table is
 * selected, so it is not one of a tablist's one-selected-at-a-time (invariant 20).
 */
function TrollBoxToggle({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      aria-expanded={open}
      aria-controls="troll-box"
      onClick={onToggle}
      className={cx(
        'relative inline-flex items-center gap-1 whitespace-nowrap text-body',
        'transition-colors duration-[var(--ud-duration-fast)]',
        'focus-visible:shadow-focus focus-visible:outline-none',
        open ? 'text-text-primary' : 'text-text-secondary hover:text-text-primary',
      )}
    >
      Troll box
      {/* Which way it goes: right to fold away, left to come back. */}
      <svg viewBox="0 0 16 16" className="size-4 shrink-0 text-text-tertiary" fill="none" aria-hidden="true">
        <path
          d={open ? 'M6 4l4 4-4 4' : 'M10 4l-4 4 4 4'}
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span
        aria-hidden="true"
        className={cx(
          'absolute inset-x-0 bottom-0 h-0_5 transition-opacity duration-[var(--ud-duration-fast)]',
          open ? 'bg-accent-solid opacity-100' : 'opacity-0',
        )}
      />
    </button>
  )
}

function readTimeframe(): Timeframe {
  try {
    const held = globalThis.localStorage?.getItem(TIMEFRAME_KEY)
    return (TIMEFRAMES as readonly string[]).includes(held ?? '') ? (held as Timeframe) : DEFAULT_TIMEFRAME
  } catch {
    return DEFAULT_TIMEFRAME
  }
}

function numberOf(source: unknown, field: string): number | null {
  const value = (source as Record<string, unknown> | null)?.[field]
  return typeof value === 'number' ? value : null
}

function stringOf(source: unknown, field: string): string | null {
  const value = (source as Record<string, unknown> | null)?.[field]
  return typeof value === 'string' ? value : null
}

/**
 * A CTF position id off the `Market` payload.
 *
 * A **string**, not a number: a `uint256` does not fit an IEEE double, so
 * `numberOf` would silently return a different token id — and the position
 * bought would be neither UP nor DOWN but one that does not exist. That is why
 * `openapi.CtfTokenId` is a decimal string and why this accessor is separate.
 */
function tokenIdOf(source: unknown, field: string): string | null {
  const value = (source as Record<string, unknown> | null)?.[field]
  return typeof value === 'string' && /^[0-9]{1,78}$/.test(value) ? value : null
}

function outcomeOf(market: unknown): 'UP' | 'DOWN' | null {
  const outcome = (market as { resolution?: { outcome?: unknown } } | null)?.resolution?.outcome
  return outcome === 'UP' || outcome === 'DOWN' ? outcome : null
}
